import { IKeyValue } from 'motw_core/Entity/IKeyValue';
import { IsDefaultKeyValue } from 'source/app/infrastructure/Data/KeyValues/defaultKeyValue';
import { ESharedFunction } from 'motw_core/Enums/ESharedFunction';
import { integratedPlusEmotionCharacter } from 'motw_core/Repositories/EmotionRepository';
import { ReadState } from '../../services/UI/State/ReadState';
import { Emotion } from './Emotion';
import { IEmotion } from './IEmotion';

const Angry = new Emotion({
	character: 'A',
	tooltip: `Angry - Block / Walk Away / Survive`,
	hexColor: '#ff5555',
	shieldHueRotateDegrees: -48,
	keystrokesFancyName: `spiders avoided`
});

const Tired = new Emotion({
	character: 'T',
	tooltip: `Tired - Block / Walk Away / Survive`,
	hexColor: '#FFA500',
	shieldHueRotateDegrees: -15,
	keystrokesFancyName: `naps taken`
});

const Uncertain = new Emotion({
	character: 'U',
	tooltip: `Uncertain - Isolate / Comfort / Stabilize mind`,
	hexColor: '#FFFF00',
	shieldHueRotateDegrees: 12,
	keystrokesFancyName: `aeons waited`
});

const Complacent = new Emotion({
	character: 'C',
	tooltip: `Complacent - Isolate / Comfort / Stabilize mind`,
	hexColor: '#baf14a',
	shieldHueRotateDegrees: 49,
	keystrokesFancyName: `cups of tea drunk`
});

const Flow = new Emotion({
	character: 'F',
	tooltip: `Flow - Think / Solve problems`,
	hexColor: '#6fffff',
	shieldHueRotateDegrees: 130,
	keystrokesFancyName: `rivers crossed`
});

const HighFlow = new Emotion({
	character: '^',
	tooltip: `High Flow - Think / Solve problems`,
	hexColor: '#8296ff',
	shieldHueRotateDegrees: 183,
	keystrokesFancyName: `shits taken`
});

const Peace = new Emotion({
	character: 'P',
	tooltip: `Peace - Get closer to God`,
	hexColor: '#b99cff',
	shieldHueRotateDegrees: 235,
	keystrokesFancyName: `stars counted`
});

const Integrated = new Emotion({
	character: 'I',
	tooltip: `Integrated - Get closer to God`,
	hexColor: '#ffffff',
	shieldHueRotateDegrees: 278,
	keystrokesFancyName: `lifetimes lived`
});

const IntegratedPlus = new Emotion({
	character: integratedPlusEmotionCharacter,
	tooltip: `Integrated+ - Maximum state`,
	hexColor: '#fff4c2',
	shieldHueRotateDegrees: 278,
	keystrokesFancyName: `lifetimes transcended`
});

const emotions: Readonly<Emotion>[] = [
	Angry,
	Tired,
	Uncertain,
	Complacent,
	Flow,
	HighFlow,
	Peace,
	Integrated,
	IntegratedPlus
];

const legacyEmotions = emotions.slice(0, -1);

export const Emotions = {
	get Current() {
		const emotionCharacterKeyValue = ReadState<IKeyValue>(
			ESharedFunction.EmotionSelector_character
		);

		if (
			emotionCharacterKeyValue === undefined ||
			IsDefaultKeyValue(emotionCharacterKeyValue)
		) {
			return Emotions.Default;
		}

		return Emotions.GetFromCharacter(emotionCharacterKeyValue.value);
	},
	get Default() {
		return Complacent;
	},
	Tired,
	Uncertain,
	Complacent,
	Flow,
	HighFlow,
	Peace,
	Integrated,
	IntegratedPlus,
	get All() {
		return emotions;
	},
	get LegacyAll() {
		return legacyEmotions;
	},
	IndexOf: (emotion: IEmotion) =>
		Emotions.All.map((e) => e.character).indexOf(emotion.character),
	Before: (emotion: IEmotion) =>
		Emotions.All.slice(0, Emotions.IndexOf(emotion)),
	After: (emotion: IEmotion) =>
		Emotions.All.slice(Emotions.IndexOf(emotion) + 1, Emotions.All.length),
	GetFromCharacter: (emotionCharacter: string) =>
		Emotions.All.find((e) => e.character === emotionCharacter)!
};

export function GetEmotionColor(emotionCharacter: string) {
	const color = Emotions.GetFromCharacter(emotionCharacter).hexColor;
	return color;
}
